import { useState } from 'react';
import { ChevronLeft, Loader2, Camera, ChevronDown, Trash2, AlertTriangle, X, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import supabase from '../../lib/supabase';

interface PersonalInfoScreenProps {
  onNavigate: (screen: any) => void;
}

const countryCodes = [
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+974', country: 'Qatar', flag: '🇶🇦' },
  { code: '+965', country: 'Kuwait', flag: '🇰🇼' },
  { code: '+968', country: 'Oman', flag: '🇴🇲' },
  { code: '+973', country: 'Bahrain', flag: '🇧🇭' },
  { code: '+20', country: 'Egypt', flag: '🇪🇬' },
  { code: '+962', country: 'Jordan', flag: '🇯🇴' },
  { code: '+961', country: 'Lebanon', flag: '🇱🇧' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+92', country: 'Pakistan', flag: '🇵🇰' },
  { code: '+880', country: 'Bangladesh', flag: '🇧🇩' },
  { code: '+63', country: 'Philippines', flag: '🇵🇭' },
];

export default function PersonalInfoScreen({ onNavigate }: PersonalInfoScreenProps) {
  const { user, setUser } = useAuthStore();
  const [fullName, setFullName] = useState(user?.user_metadata?.full_name || '');

  // Parse existing phone into country code and number
  const existingPhone = user?.user_metadata?.phone || '';
  const existingCountryCode = countryCodes.find(c => existingPhone.startsWith(c.code));
  const [countryCode, setCountryCode] = useState(existingCountryCode?.code || '+971');
  const [phoneNumber, setPhoneNumber] = useState(
    existingCountryCode ? existingPhone.replace(existingCountryCode.code, '').trim() : existingPhone
  );
  const [showCountryPicker, setShowCountryPicker] = useState(false);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!fullName.trim()) {
      setError('Name is required');
      return;
    }

    if (!phoneNumber.trim()) {
      setError('Phone number is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const fullPhone = `${countryCode} ${phoneNumber}`;

      const { data: updatedUser, error: updateError } = await supabase.auth.updateUser({
        data: {
          full_name: fullName,
          phone: fullPhone,
        }
      });

      if (updateError) throw updateError;

      // Update profile table
      await supabase
        .from('profiles')
        .upsert({
          user_id: user?.id,
          full_name: fullName,
          phone: fullPhone,
        });

      // Refresh the user in the auth store
      if (updatedUser?.user) {
        setUser(updatedUser.user);
      }

      setSuccess(true);
      setTimeout(() => {
        onNavigate('profile');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="w-full h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-[#D4F04A] mx-auto mb-4 flex items-center justify-center">
            <span className="text-4xl">✓</span>
          </div>
          <h2 className="text-xl mb-2">Profile Updated!</h2>
          <p className="text-muted-foreground">Your information has been saved</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-background pb-24">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onNavigate('profile')}
            className="w-10 h-10 rounded-full hover:bg-secondary transition-colors flex items-center justify-center"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg">Personal Information</h1>
            <p className="text-xs text-muted-foreground">Update your details</p>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 space-y-6">
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="flex justify-center">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-[#D4F04A] flex items-center justify-center text-4xl">
              👤
            </div>
            <button className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[#1A6B4F] text-white flex items-center justify-center">
              <Camera className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div>
          <label className="text-sm text-muted-foreground mb-2 block">Email Address</label>
          <input
            type="email"
            value={user?.email || ''}
            disabled
            className="w-full h-12 px-4 rounded-xl bg-secondary border border-border text-muted-foreground cursor-not-allowed"
          />
          <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
        </div>

        <div>
          <label className="text-sm text-muted-foreground mb-2 block">Full Name</label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="John Doe"
            className="w-full h-12 px-4 rounded-xl bg-input-background border border-transparent focus:border-[#1A6B4F] focus:outline-none"
          />
        </div>

        <div>
          <label className="text-sm text-muted-foreground mb-2 block">Phone Number</label>
          <div className="relative flex gap-2">
            <button
              type="button"
              onClick={() => setShowCountryPicker(!showCountryPicker)}
              className="relative h-12 px-3 rounded-xl bg-input-background border border-transparent hover:border-[#1A6B4F] transition-colors flex items-center gap-2 flex-shrink-0"
            >
              <span className="text-xl">{countryCodes.find(c => c.code === countryCode)?.flag}</span>
              <span className="text-sm">{countryCode}</span>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </button>

            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="50 123 4567"
              className="flex-1 h-12 px-4 rounded-xl bg-input-background border border-transparent focus:border-[#1A6B4F] focus:outline-none"
            />
          </div>

          {showCountryPicker && (
            <div className="absolute z-50 mt-2 w-full max-w-xs bg-card border border-border rounded-xl shadow-lg max-h-64 overflow-y-auto">
              {countryCodes.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => {
                    setCountryCode(country.code);
                    setShowCountryPicker(false);
                  }}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-secondary transition-colors text-left"
                >
                  <span className="text-xl">{country.flag}</span>
                  <span className="flex-1 text-sm">{country.country}</span>
                  <span className="text-sm text-muted-foreground">{country.code}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="bg-secondary rounded-xl p-4">
          <h3 className="text-sm font-medium mb-2">Account Information</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">User ID</span>
              <span className="text-xs font-mono">{user?.id?.substring(0, 8)}...</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Account Created</span>
              <span>{new Date(user?.created_at || '').toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        <ChangePassword />

        <DangerZone onDone={() => onNavigate('home')} />
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border p-4">
        <button
          onClick={handleSave}
          disabled={loading}
          className="w-full h-14 rounded-2xl bg-[#1A6B4F] hover:bg-[#14563F] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>
    </div>
  );
}

/**
 * Self-service account deletion. Uses the delete-account edge function,
 * which refuses if the user has active bookings or manages a club /
 * coach profile. Requires typing DELETE to confirm.
 */
function DangerZone({ onDone }: { onDone: () => void }) {
  const { signOut } = useAuthStore();
  const [showModal, setShowModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const confirmed = confirmText.trim().toUpperCase() === 'DELETE';

  const submit = async () => {
    if (!confirmed) return;
    setBusy(true); setErr(null);
    const { data, error } = await supabase.functions.invoke('delete-account', { body: {} });
    setBusy(false);
    if (error) {
      setErr((error as any).message || 'Account deletion failed.');
      return;
    }
    if (data && (data as any).error) {
      setErr((data as any).error);
      return;
    }
    await signOut();
    onDone();
  };

  return (
    <>
      <div className="mt-6 rounded-xl border border-red-200 bg-red-50/60 p-4">
        <h3 className="text-sm font-medium text-red-900 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Danger zone
        </h3>
        <p className="text-xs text-red-900/80 mt-1 leading-relaxed">
          Deleting your account removes your profile, saved addresses, favourites,
          and notifications. Past bookings and reviews stay on file, anonymised.
          This cannot be undone.
        </p>
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className="mt-3 h-10 px-3 rounded-lg bg-white border border-red-300 text-red-700 text-sm flex items-center gap-1.5 hover:bg-red-50"
        >
          <Trash2 className="w-3.5 h-3.5" /> Delete my account
        </button>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50">
          <div className="bg-card w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 relative">
            <button onClick={() => setShowModal(false)} aria-label="Close"
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
            <h3 className="text-lg font-semibold text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Delete account
            </h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Type <span className="font-mono font-medium text-foreground">DELETE</span> below
              to confirm. We'll sign you out and remove your profile from Deuce.
            </p>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE"
              className="mt-3 w-full h-11 px-3 rounded-lg border border-border text-sm bg-background uppercase tracking-widest"
            />
            {err && (
              <div className="mt-3 text-sm rounded-lg p-2.5 bg-red-50 border border-red-200 text-red-700 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{err}</span>
              </div>
            )}
            <div className="mt-4 flex gap-2">
              <button type="button" onClick={() => setShowModal(false)}
                className="flex-1 h-11 rounded-lg border border-border text-sm">Keep my account</button>
              <button type="button" onClick={submit} disabled={!confirmed || busy}
                className="flex-1 h-11 rounded-lg bg-red-600 text-white text-sm flex items-center justify-center gap-1.5 disabled:opacity-40">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete forever
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Self-service password change. Supabase enforces a signed-in session here —
 * no current-password re-entry is required at the API level, but we add the
 * "confirm new password" field to guard against typos.
 */
function ChangePassword() {
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const submit = async () => {
    setMsg(null);
    if (next.length < 8) { setMsg({ kind: 'error', text: 'At least 8 characters.' }); return; }
    if (next !== confirm) { setMsg({ kind: 'error', text: 'Passwords don\u2019t match.' }); return; }
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password: next });
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error.message }); return; }
    setNext(''); setConfirm('');
    setMsg({ kind: 'success', text: 'Password updated.' });
  };

  return (
    <div className="mt-6 rounded-xl border border-border p-4">
      <h3 className="text-sm font-medium flex items-center gap-2">
        <Lock className="w-4 h-4" /> Change password
      </h3>
      <p className="text-xs text-muted-foreground mt-1">
        Used the next time you sign in. Minimum 8 characters.
      </p>
      <div className="mt-3 space-y-2">
        <div className="relative">
          <input
            type={show ? 'text' : 'password'}
            value={next} onChange={(e) => setNext(e.target.value)}
            placeholder="New password"
            autoComplete="new-password"
            className="w-full h-11 px-3 pr-10 rounded-lg border border-border text-sm bg-background"
          />
          <button type="button" onClick={() => setShow((s) => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
            aria-label={show ? 'Hide password' : 'Show password'}>
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <input
          type={show ? 'text' : 'password'}
          value={confirm} onChange={(e) => setConfirm(e.target.value)}
          placeholder="Confirm new password"
          autoComplete="new-password"
          className="w-full h-11 px-3 rounded-lg border border-border text-sm bg-background"
        />
        {msg && (
          <div className={`text-xs rounded-lg p-2.5 ${msg.kind === 'success'
            ? 'bg-green-50 border border-green-200 text-green-800'
            : 'bg-red-50 border border-red-200 text-red-700'}`}>
            {msg.text}
          </div>
        )}
        <button type="button" onClick={submit} disabled={busy || !next}
          className="w-full h-10 rounded-lg bg-[#1A6B4F] hover:bg-[#14563F] text-white text-sm flex items-center justify-center gap-2 disabled:opacity-50">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          Update password
        </button>
      </div>
    </div>
  );
}
